<script lang="ts">
  import { onMount } from 'svelte';
  import { UserService, type EmployerOrganizationUpdateData, type EmployerUpdateResponse } from '../../lib/api/user.service';
  import { userStore } from '../../stores/auth.store';
  import { AuthService, type User } from '../../lib/api/auth.service';
  import Button from '../../components/Button.svelte';
  import Input from '../../components/Input.svelte';
  import toast from 'svelte-french-toast';

  let loading = false;
  let initializing = true;
  let fieldErrors: Record<string, string[]> = {};
  let user: User | null = null;
  let employerData: any = null;

  // Tab state
  let activeTab: 'email' | 'profile' = 'profile';

  // Email change modal state
  let showEmailModal = false;
  let emailLoading = false;
  let emailData = {
    email: ''
  };

  // Employer organization data
  let organizationData: EmployerOrganizationUpdateData = {
    name: '',
    abn: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    head_office_location: ''
  };

  // Load user data on mount
  onMount(async () => {
    await loadUserData();
  });

  async function loadUserData() {
    try {
      initializing = true;
      const localUser = AuthService.getUser();
      
      if (localUser) {
        user = localUser;
        initializeEmployerData();
      } else {
        const result = await AuthService.getCurrentUser();
        if (result.success && result.user) {
          user = result.user;
          initializeEmployerData();
        } else {
          toast.error('Failed to load user data');
        }
      }
    } catch (error: any) {
      console.error('Error loading user:', error);
      toast.error('Failed to load user data');
    } finally {
      initializing = false;
    }
  }

  function initializeEmployerData() {
    if (!user) return;
    
    try {
      // Get employer data from user.organization.data
      if (user?.organization?.type === 'employer' && user?.organization?.data) {
        employerData = user.organization.data;
        
        // Initialize organization data
        organizationData = {
          name: employerData?.name || '',
          abn: employerData?.abn || '',
          contact_name: employerData?.contact_name || '',
          contact_email: employerData?.contact_email || '',
          contact_phone: employerData?.contact_phone || '',
          head_office_location: employerData?.head_office_location || ''
        };
      } else {
        toast.error('No employer associated with this user');
      }
    } catch (error: any) {
      console.error('Failed to initialize employer data:', error);
      toast.error('Failed to load employer information');
    }
  }

  function getFieldError(fieldName: string): string | undefined {
    return fieldErrors[fieldName] ? fieldErrors[fieldName][0] : undefined;
  }

  // Validation functions
  function validateEmail(email: string): string | null {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  }
  
  function validatePhone(phone: string): string | null {
    if (!phone) return null;
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number';
    return null;
  }
  
  function validateABN(abn: string): string | null {
    if (!abn) return 'ABN is required';
    const cleanABN = abn.replace(/\s/g, '');
    if (cleanABN.length !== 11) return 'ABN must be 11 digits';
    if (!/^\d+$/.test(cleanABN)) return 'ABN must contain only numbers';
    return null;
  }
  
  function validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  }

  function validateForm(): boolean {
    const errors: Record<string, string[]> = {};
    
    // Validate required fields
    const nameError = validateRequired(organizationData.name || '', 'Employer name');
    if (nameError) errors.name = [nameError];
    
    const abnError = validateABN(organizationData.abn || '');
    if (abnError) errors.abn = [abnError];
    
    // Optional field validations
    if (organizationData.contact_email) {
      const contactEmailError = validateEmail(organizationData.contact_email);
      if (contactEmailError) errors.contact_email = [contactEmailError];
    }
    
    if (organizationData.contact_phone) {
      const contactPhoneError = validatePhone(organizationData.contact_phone);
      if (contactPhoneError) errors.contact_phone = [contactPhoneError];
    }
    
    fieldErrors = errors;
    
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ');
      toast.error(`Please fix the following errors: ${errorMessages}`);
      return false;
    }
    
    return true;
  }

  async function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    loading = true;
    fieldErrors = {};
    
    try {
      const result: EmployerUpdateResponse = await UserService.updateEmployerOrganization(organizationData);
      
      if (result.success) {
        // Update user object with new employer data
        if (user && user.organization && user.organization.data && result.employer) {
          const updatedUser = {
            ...user,
            organization: {
              ...user.organization,
              data: {
                ...user.organization.data,
                ...result.employer
              }
            }
          };
          
          // Update localStorage and user store
          localStorage.setItem('user', JSON.stringify(updatedUser));
          userStore.set(updatedUser as User);
          user = updatedUser as User;
          employerData = result.employer;
        }
        
        toast.success(result.message || 'Employer organization updated successfully!');
      } else {
        // Handle UserService error response
        if (result.error?.response?.data?.data?.errors) {
          const errors = result.error.response.data.data.errors;
          fieldErrors = errors;
        }
        toast.error(result.message || 'Failed to update employer information');
      }
    } catch (error: any) {
      console.error('Employer update error:', error);
      
      if (error?.response?.data?.data?.errors) {
        const errors = error.response.data.data.errors;
        fieldErrors = errors;
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update employer information');
      }
    } finally {
      loading = false;
    }
  }

  function openEmailModal() {
    emailData = {
      email: user?.email || ''
    };
    showEmailModal = true;
  }

  function closeEmailModal() {
    showEmailModal = false;
    emailData = { email: '' };
  }

  async function handleEmailUpdate() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!emailRegex.test(emailData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    emailLoading = true;
    try {
      const result = await AuthService.updateEmail({
        email: emailData.email
      });
      
      if (result.success) {
        toast.success('Email sent successfully! Please check your email address for verification link.');
        closeEmailModal();
      } else {
        toast.error((result as any)?.error?.response?.data?.data?.errors?.email || 'Failed to send email. Please try again.');
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        toast.error(errorMessages);
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      emailLoading = false;
    }
  }

  function handleEmailModalKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeEmailModal();
    } else if (event.key === 'Enter' && !emailLoading) {
      handleEmailUpdate();
    }
  }
</script>

<div class="profile-page">
  {#if initializing}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading profile...</p>
    </div>
  {:else}
    <div class="page-header">
      <h1>Edit Employer Profile</h1>
      <p>Manage your employer organization settings and information</p>
    </div>
    
    <!-- Tabs Navigation -->
    <div class="tabs-container">
      <div class="tabs">
        <button 
          class="tab-button" 
          class:active={activeTab === 'profile'} 
          on:click={() => activeTab = 'profile'}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          Profile Information
        </button>
        <button 
          class="tab-button" 
          class:active={activeTab === 'email'} 
          on:click={() => activeTab = 'email'}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          Primary Email
        </button>
      </div>
    </div>
    
    <!-- Tab Content -->
    <div class="tab-content-wrapper">
      {#if activeTab === 'email'}
        <!-- Primary Email Tab -->
        <div class="profile-container">
          <div class="section">
            <h2>Email Address</h2>
            <p class="section-description">Update your account email address. A verification email will be sent to confirm the change.</p>
            
            <div class="email-section">
              <div class="current-email-card">
                <div class="current-email-header">
                  <svg class="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <h3>Current Primary Email</h3>
                </div>
                <div class="current-email-value">
                  {user?.email || 'Not provided'}
                </div>
              </div>
              
              <div class="email-actions">
                <Button 
                  variant="primary" 
                  on:click={openEmailModal}
                  disabled={loading || emailLoading}
                >
                  Change Email
                </Button>
              </div>
            </div>
          </div>
        </div>
      {:else}
        <!-- Profile Information Tab -->
        <div class="profile-container">
          <form on:submit|preventDefault={handleSubmit}>
            <div class="section">
              <h2>Employer Organization Information</h2>
              
              <div class="form-row">
                <div class="form-group">
                  <Input 
                    label="Employer Name"
                    placeholder="Enter employer/company name"
                    bind:value={organizationData.name}
                    required={true}
                    error={getFieldError('name')}
                    disabled={loading}
                  />
                </div>
                
                <div class="form-group">
                  <Input 
                    label="ABN"
                    placeholder="Enter ABN"
                    bind:value={organizationData.abn}
                    required={true}
                    error={getFieldError('abn')}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <Input 
                    label="Contact Person Name"
                    placeholder="Enter primary contact person name"
                    bind:value={organizationData.contact_name}
                    error={getFieldError('contact_name')}
                    disabled={loading}
                  />
                </div>
                
                <div class="form-group">
                  <Input 
                    label="Head Office Location"
                    placeholder="Enter head office address/location"
                    bind:value={organizationData.head_office_location}
                    error={getFieldError('head_office_location')}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
            
            <div class="section">
              <h2>Contact Information</h2>
              
              <div class="form-row">
                <div class="form-group">
                  <Input 
                    label="Contact Email"
                    type="email"
                    placeholder="Contact email (optional)"
                    bind:value={organizationData.contact_email}
                    error={getFieldError('contact_email')}
                    disabled={loading}
                  />
                </div>
                
                <div class="form-group">
                  <Input 
                    label="Contact Phone"
                    type="tel"
                    placeholder="Contact phone (optional)"
                    bind:value={organizationData.contact_phone}
                    error={getFieldError('contact_phone')}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
            
            <div class="info-section">
              <div class="info-card">
                <h4>ℹ️ Organization Information</h4>
                <p>Update your employer organization details. Required fields are marked with an asterisk (*).</p>
              </div>
            </div>
            
            <div class="form-actions">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Organization'}
              </Button>
            </div>
          </form>
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- Email Change Modal -->
{#if showEmailModal}
  <div 
    class="modal-overlay" 
    role="presentation"
    on:click|self={closeEmailModal}
    on:keydown={(e) => e.key === 'Escape' && closeEmailModal()}
  >
    <div 
      class="modal-content" 
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
    >
      <div class="modal-header">
        <h3 id="email-modal-title">Change Email Address</h3>
        <button class="modal-close" on:click={closeEmailModal}>×</button>
      </div>
      
      <div class="modal-body">
        <div class="form-group">
          <Input 
            type="email"
            label="New Email Address"
            bind:value={emailData.email}
            placeholder="Enter your new email address"
            required={true}
            fullWidth={true}
            disabled={emailLoading}
          />
        </div>
        
        <p class="info-text">
          📧 An email will be sent to the specified email address with a verification link to update your email.
        </p>
      </div>
      
      <div class="modal-footer">
        <Button 
          variant="secondary" 
          on:click={closeEmailModal}
          disabled={emailLoading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          on:click={handleEmailUpdate}
          disabled={emailLoading || !emailData.email.trim()}
        >
          {emailLoading ? 'Sending...' : 'Send Verification Email'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .profile-page {
    min-height: 100vh;
    background-color: #f7fafc;
    padding: 1.5rem 2rem;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 1rem;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .loading-container p {
    color: #6b7280;
    font-size: 0.875rem;
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .page-header h1 {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .page-header p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  /* Tabs */
  .tabs-container {
    margin-bottom: 2rem;
    max-width: 800px;
  }

  .tabs {
    display: flex;
    gap: 0.5rem;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 0;
  }

  .tab-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -2px;
  }

  .tab-button:hover {
    color: #111827;
  }

  .tab-button.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
  }

  .tab-button svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .tab-content-wrapper {
    max-width: 800px;
  }

  /* Email Section */
  .email-section {
    margin-top: 1.5rem;
  }

  .current-email-card {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .current-email-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .current-email-header h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
  }

  .email-icon {
    color: var(--color-primary);
    flex-shrink: 0;
  }

  .current-email-value {
    font-size: 1.125rem;
    font-weight: 500;
    color: #111827;
    word-break: break-all;
    padding: 0.75rem 1rem;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
  }

  .email-actions {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
  }

  .section-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem 0;
    line-height: 1.5;
  }

  .profile-container {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    padding: 2rem;
    max-width: 800px;
  }

  .section {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .section:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-row:last-child {
    margin-bottom: 0;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .info-section {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e7eb;
  }

  .info-card {
    background: #fffbeb;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    padding: 1.25rem;
  }

  .info-card h4 {
    color: #d97706;
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-card p {
    color: #92400e;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e7eb;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;
  }

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
    color: white;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .modal-body {
    padding: 2rem;
  }

  .modal-body .form-group {
    margin-bottom: 1rem;
  }

  .info-text {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  @media (max-width: 768px) {
    .profile-page {
      padding: 1rem;
    }

    .tabs {
      gap: 0.25rem;
      overflow-x: auto;
    }

    .tab-button {
      padding: 0.75rem 1rem;
      font-size: 0.813rem;
      white-space: nowrap;
    }

    .tab-button svg {
      width: 14px;
      height: 14px;
    }

    .current-email-card {
      padding: 1.25rem;
    }

    .current-email-value {
      font-size: 1rem;
    }

    .profile-container {
      padding: 1.5rem;
    }

    .form-row {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .page-header h1 {
      font-size: 1.5rem;
    }
  }
</style>
